import React, { useContext, useState, useCallback } from 'react';
import { observer } from 'mobx-react';

import ExternalCoachContext from '../../../../../../context/ExternalCoachContext';
import useToast from '../../../../../../hooks/useToast';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import RadioButtonGroup from '../../../../../../components/RadioButtonGroup';
import { SettingOption } from '../../utils';
import {
  SettingContainer as Container,
  HeaderContainer,
  TitleContainer,
  Title,
  SubTitle,
  SettingsActionContainer,
  ActionLabel,
} from '../WorkoutNotification/styles';
import {
  NotConnectedBanner,
  NotConnectedText,
} from './styles';

import texts from './texts.json';

const RADIO_OPTIONS = [
  { label: texts.enabled, value: SettingOption.ENABLED },
  { label: texts.disabled, value: SettingOption.DISABLED },
];

const TOGGLES = [
  { field: 'newSubscription', label: texts.newSubscription, defaultOn: true },
  { field: 'disputes', label: texts.disputes, defaultOn: true },
  { field: 'failedPayment', label: texts.failedPayment, defaultOn: false },
  { field: 'pause', label: texts.pause, defaultOn: false },
  { field: 'cancellation', label: texts.cancellation, defaultOn: false },
  { field: 'upsell', label: texts.upsell, defaultOn: false },
  { field: 'amendment', label: texts.amendment, defaultOn: false },
];

const getInitialState = (config) => Object.fromEntries(
  TOGGLES.map(({ field, defaultOn }) => [
    field,
    (defaultOn ? config[field] !== false : config[field] === true)
      ? SettingOption.ENABLED
      : SettingOption.DISABLED,
  ]),
);

const SlackNotificationSettings = () => {
  const { coachDoc } = useContext(ExternalCoachContext);
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const config = coachDoc?.slackNotificationsConfig ?? {};
  const isSlackConnected = !!(coachDoc?.isSlackNotificationsEnabled && coachDoc?.slackChannelId);

  const [values, setValues] = useState(() => getInitialState(config));

  const handleChange = useCallback(
    async (field, value) => {
      const previousValue = values[field];
      setValues((prev) => ({ ...prev, [field]: value }));
      try {
        await coachDoc.updateSlackNotificationsConfig({
          [field]: value === SettingOption.ENABLED,
        });
        if (isComponentMountedRef.current) {
          showToast(texts.saveSuccess);
        }
      } catch (error) {
        if (isComponentMountedRef.current) {
          setValues((prev) => ({ ...prev, [field]: previousValue }));
          showToast(texts.saveError, { error: true });
        }
      }
    },
    [coachDoc, isComponentMountedRef, showToast, values],
  );

  if (!coachDoc) {
    return null;
  }

  return (
    <Container>
      <HeaderContainer>
        <TitleContainer>
          <Title>{texts.title}</Title>
          <SubTitle>{texts.description}</SubTitle>
        </TitleContainer>
      </HeaderContainer>

      {!isSlackConnected && (
        <NotConnectedBanner>
          <NotConnectedText>{texts.notConnected}</NotConnectedText>
        </NotConnectedBanner>
      )}

      {isSlackConnected && TOGGLES.map(({ field, label }) => (
        <SettingsActionContainer key={field}>
          <ActionLabel>{label}</ActionLabel>
          <RadioButtonGroup
            options={RADIO_OPTIONS}
            selectedOption={values[field]}
            onOptionChange={(value) => handleChange(field, value)}
          />
        </SettingsActionContainer>
      ))}
    </Container>
  );
};

export default observer(SlackNotificationSettings);
export { TOGGLES, getInitialState };
